import { Fragment, useMemo, useState } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { routerHandlesClick } from '../../../../lib/use-in-app-link';
import { useBasePath } from '../../../../lib/use-base-path';
import { queryKeys } from '../../../../lib/query-client';
import { connectorDisplayName, fetchConnectors, type ConnectorStatus } from '../../../../lib/connectors';
import { patchRoutine, useRoutines, type WebRoutine } from '../../../../lib/routines';
import { type PostConnectedConnectorCardData } from '../../../../lib/post-connected-connector-card';
import {
  activeRecommendedSplit,
  connectorCardPages,
  connectorPageRoutines,
  connectorRoutinesForSlugs,
  connectorNameOverride,
  connectorSlugName,
  promiseSections,
  routineGroupTitleKey,
  turnOnAllState,
} from '../../../../lib/routine-groups';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { useRoutineCopy } from '../../routines/use-routine-copy';
import { Badge, Card, IconButton, Switch, TextButton } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './PostConnectedConnectorCard.module.css';

/**
 * What a connected app brought the user, rendered UNDER an assistant reply carrying
 * `metadata.postConnectedConnectorCard` — a sibling of `ConnectorConnectCard` and `OpportunityCard`,
 * riding its reply the same way. Figma 18942-127886 (desktop) / 18942-128565 (mobile).
 *
 * The sibling it is most easily confused with is the connect card, and they are opposites: that one
 * offers a connection that has not happened and expires, this one describes an account that is
 * already set up. Hence the separate metadata key rather than a mode on one card.
 *
 * <h2>The rows are LIVE, not payload</h2>
 *
 * The message carries the app's identity and the home-widget count; the switch rows come from
 * `useRoutines()`, filtered to one connector. A switch drawn from the turn's snapshot shows the wrong
 * state the moment anything else moves it — the Routines screen, another device, the agent itself. So a
 * card in an old message shows today's routines, and a deleted routine simply has no row.
 *
 * <h2>Pages</h2>
 *
 * The card opens on its own app and pages through every other app that has routines
 * (`connectorCardPages`), in the Connectors screen's order. Only the own app's page can say how many
 * home widgets landed: that count is the one thing the message carries, and no client can compute it
 * for another app. The summary the connect produced (`card.summary`) is parsed but not drawn — the
 * reply above the card says it in words.
 *
 * The band card still draws its app rows with `ConnectorRoutineRows` (Active / Recommended); this card
 * groups by promise instead, per its own frame.
 */
export function PostConnectedConnectorCard({ card }: { card: PostConnectedConnectorCardData }) {
  const { t } = useTranslation();
  const { state } = useRoutines();
  const { logPageClickBi } = useGenericBi();
  const { scheduleText } = useRoutineCopy();
  const leaveChat = useLeaveChatOnNavigate();
  const basePath = useBasePath();
  const connectorsQuery = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  const [inFlight, setInFlight] = useState<Record<string, boolean>>({});

  const allRoutines = state.kind === 'ready' ? state.routines : [];
  const connected = useMemo(
    () => (connectorsQuery.data?.connectors ?? []).filter((connector) => connector.connected === true),
    [connectorsQuery.data],
  );
  const ownSlug = card.toolkit.trim().toLowerCase();
  const widgetCount = card.homeWidgets?.length ?? 0;
  const pages = useMemo(
    () =>
      connectorCardPages(
        allRoutines,
        card.toolkit,
        connected.map((connector) => connector.toolkit ?? ''),
        widgetCount > 0,
      ),
    [allRoutines, card.toolkit, connected, widgetCount],
  );
  // The picked page is held by SLUG, not index: a routine landing for a new app re-orders the pages, and
  // an index would silently move the reader to a different app under their hand.
  const [pickedSlug, setPickedSlug] = useState(ownSlug);
  const pageIndex = Math.max(0, pages.includes(pickedSlug) ? pages.indexOf(pickedSlug) : pages.indexOf(ownSlug));
  const slug = pages[pageIndex] ?? ownSlug;
  const isOwnPage = slug === ownSlug;

  const pageApp = (pageSlug: string) => appIdentity(pageSlug, ownSlug, card, connected);
  const app = pageApp(slug);
  const routines = useMemo(() => connectorPageRoutines(allRoutines, slug), [allRoutines, slug]);
  const sections = useMemo(() => promiseSections(routines), [routines]);
  const { active, recommended } = useMemo(() => activeRecommendedSplit(routines), [routines]);
  const masterOn = turnOnAllState(routines);

  const seenRef = useItemSeenBi('post_connected_connector_card', {
    additionalFields: { toolkit: card.toolkit, routines: String(routines.length), homeWidgets: String(widgetCount) },
  });

  const toggle = async (routine: WebRoutine) => {
    if (!routine.id || inFlight[routine.id]) return;
    setInFlight((current) => ({ ...current, [routine.id]: true }));
    // Optimistic and rolls back on failure, so a refused flip leaves the switch showing what really runs.
    await patchRoutine(routine, { paused: !routine.paused });
    setInFlight((current) => ({ ...current, [routine.id]: false }));
  };

  // The master switch is N single toggles, not one write: a refused flip rolls back its own row and the
  // rest still land, so the section can never end half-done with no record of which flips took.
  const flipAll = (turnOn: boolean) => {
    const targets = turnOn ? recommended : active;
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'connector-routines-turn-on-all' },
      { additionalFields: { count: String(targets.length), to: turnOn ? 'on' : 'off', toolkit: slug } },
    );
    for (const routine of targets) void toggle(routine);
  };
  const bulkInFlight = [...active, ...recommended].some((routine) => routine.id && inFlight[routine.id]);

  const go = (direction: 'prev' | 'next') => {
    const next = pages[(pageIndex + (direction === 'next' ? 1 : pages.length - 1)) % pages.length];
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-page' }, { additionalFields: { direction, toolkit: next } });
    setPickedSlug(next);
  };
  const nextApp = pages.length > 1 ? pageApp(pages[(pageIndex + 1) % pages.length]) : undefined;
  const hasRows = sections.length > 0;

  if (pages.length === 0) return null;

  return (
    <Card
      component="section"
      ref={seenRef}
      className={styles.card}
      aria-label={app.name}
      data-testid="chat-post-connected-connector-card"
      data-toolkit={slug}
    >
      <div className={styles.head}>
        <div className={styles.identity}>
          {/* A synthetic status: the logo component reads a ConnectorStatus, and `connected: true` is the
              honest value — every page is an app the account has routines for. */}
          <ConnectorLogo connector={{ toolkit: slug, logo: app.logo, connected: true }} name={app.name} size={26} radius={8} glyphSize={18} />
          <span className={`sym-type-small-semi-bold ${styles.appName}`}>{app.name}</span>
        </div>
        {isOwnPage && widgetCount > 0 && (
          <Link
            href="/"
            as={`${basePath}/`}
            className={`sym-type-tiny-regular ${styles.homepageLink}`}
            data-testid="chat-post-connected-connector-card-homepage"
            onClick={(e) => {
              logPageClickBi(
                { ...CHAT_HOME_BI, button_name: 'post-connected-homepage-cards' },
                { additionalFields: { toolkit: card.toolkit, homeWidgets: String(widgetCount) } },
              );
              // A cmd/ctrl/shift-click opens a new tab; only a same-tab navigation should fold the chat away.
              if (routerHandlesClick(e)) leaveChat();
            }}
          >
            {t(`connectors.card.homepageAdded.${widgetCount === 1 ? 'one' : 'other'}`, { count: widgetCount })}
          </Link>
        )}
      </div>

      <div className={styles.titleRow}>
        <h3 className={`sym-type-display-05 ${styles.title}`}>{t('connectors.card.routinesTitle', { name: app.name })}</h3>
        {pages.length > 1 && (
          <span className={`sym-type-tiny-regular ${styles.secondary}`} data-testid="chat-post-connected-connector-card-page-of">
            {t('connectors.card.pageOf', { index: pageIndex + 1, count: pages.length })}
          </span>
        )}
      </div>

      {hasRows && (
        <>
          <hr className={styles.rule} />
          {masterOn !== null && (
            <>
              <div className={styles.masterRow}>
                <div className={styles.masterLabel}>
                  <span className="sym-type-small-semi-bold">{t('connectors.card.turnOnAllLabel')}</span>
                  <Badge variant="recommended" label={t('routines.status.recommended')} />
                </div>
                <Switch
                  checked={masterOn}
                  disabled={bulkInFlight}
                  onChange={(next) => flipAll(next)}
                  aria-label={t('connectors.card.turnOnAllLabel')}
                />
              </div>
              <hr className={styles.rule} />
            </>
          )}
          <div className={styles.sections}>
            {sections.map((section, sectionIndex) => (
              <section key={section.group} className={styles.section}>
                <span className={`sym-type-tiny-medium ${styles.sectionTitle}`}>{t(routineGroupTitleKey(section.group))}</span>
                <ul className={styles.routines}>
                  {section.routines.map((routine, rowIndex) => {
                    const schedule = scheduleText(routine);
                    const lastRow = sectionIndex === sections.length - 1 && rowIndex === section.routines.length - 1;
                    return (
                      <Fragment key={routine.id}>
                        <li className={styles.routine} data-testid={`chat-post-connected-connector-card-routine-${routine.id}`}>
                          <div className={styles.text}>
                            <span className={`sym-type-small-semi-bold ${styles.routineName}`}>{routine.name ?? ''}</span>
                            {schedule && <span className={`sym-type-small-regular ${styles.schedule}`}>{schedule}</span>}
                          </div>
                          <Switch
                            checked={!routine.paused}
                            disabled={Boolean(routine.id && inFlight[routine.id])}
                            onChange={() => void toggle(routine)}
                            aria-label={routine.name ?? ''}
                          />
                        </li>
                        {!lastRow && <li aria-hidden className={styles.rowRule} />}
                      </Fragment>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
          <p className={`sym-type-small-regular ${styles.secondary} ${styles.activeCount}`}>
            {t(`connectors.card.routinesActive.${active.length === 1 ? 'one' : 'other'}`, { count: active.length })}
          </p>
        </>
      )}

      {nextApp && (
        <>
          <hr className={`${styles.rule} ${styles.pagerRule}`} />
          <nav className={styles.pager} aria-label={t('connectors.card.routinesTitle', { name: app.name })}>
            <IconButton variant="text" size="small" aria-label={t('connectors.card.previousApp')} onClick={() => go('prev')}>
              <ArrowLeft size={16} className="dir-flip" aria-hidden />
            </IconButton>
            <TextButton
              size="small"
              className={styles.pagerLabel}
              onClick={() => go('next')}
              startIcon={
                <ConnectorLogo connector={{ toolkit: nextApp.slug, logo: nextApp.logo, connected: true }} name={nextApp.name} size={12} radius={3} glyphSize={12} tileBackground="transparent" />
              }
              data-testid="chat-post-connected-connector-card-next-label"
            >
              {t('connectors.card.seeRoutines', { name: nextApp.name })}
            </TextButton>
            <IconButton variant="text" size="small" aria-label={t('connectors.card.nextApp')} onClick={() => go('next')}>
              <ArrowRight size={16} className="dir-flip" aria-hidden />
            </IconButton>
          </nav>
        </>
      )}
    </Card>
  );
}

/** The name and logo a page shows: the Connectors screen's row when the app is connected, the card's own
 *  payload for its own app, else a name derived from the slug and the letter tile. */
export function appIdentity(
  slug: string,
  ownSlug: string,
  card: PostConnectedConnectorCardData,
  connected: ConnectorStatus[],
): { slug: string; name: string; logo?: string } {
  const row = connected.find((connector) => (connector.toolkit ?? '').toLowerCase() === slug);
  if (slug === ownSlug) return { slug, name: connectorNameOverride(slug) ?? card.name, logo: card.logoUrl ?? row?.logo };
  if (row) return { slug, name: connectorDisplayName(row), logo: row.logo };
  return { slug, name: connectorSlugName(slug) };
}

/** This connector's routines, in reading order. The rule is `connectorRoutinesForSlugs` in
 *  `lib/routine-groups` — a lib module, so it is unit-testable without importing this component. */
export function connectorRoutines(routines: WebRoutine[], toolkit: string): WebRoutine[] {
  return connectorRoutinesForSlugs(routines, [toolkit]);
}
